import { createLogger } from '@/lib/logger';

const logger = createLogger('AppUpdate');

// Commit sha baked in at build time (see vite.config.ts). Empty for local
// dev/preview builds, in which case the watcher adopts the first version.json
// it reads as its baseline instead.
export const CURRENT_BUILD_ID: string = __APP_BUILD_ID__;

// Served no-cache from the SPA root and rewritten last on each deploy, so it
// always reflects the build that is currently live behind Front Door.
const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_PROBE_DELAY_MS = 10 * 1000;

interface VersionInfo {
  sha?: string;
}

// Commit sha is the build identity (matches CURRENT_BUILD_ID when baked).
// Same-commit redeploys are intentionally ignored — they typically produce
// identical bundles and are rare compared to normal commits-on-deploy.
function fingerprint(info: VersionInfo): string | null {
  return info.sha || null;
}

async function fetchLiveVersion(): Promise<VersionInfo | null> {
  try {
    const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    // The dev server / SPA fallback answers unknown paths with index.html;
    // ignore anything that isn't actually the JSON marker.
    if (!(res.headers.get('content-type') ?? '').includes('json')) return null;
    return (await res.json()) as VersionInfo;
  } catch {
    return null;
  }
}

// --- Update-prompt snooze --------------------------------------------------
// When the user dismisses the "new version" toast, suppress it for a while so a
// long-lived session isn't nagged repeatedly. Persisted to localStorage so the
// snooze also survives reloads within the window.
const UPDATE_SNOOZE_KEY = 'crimson:updateSnoozeUntil';
const UPDATE_SNOOZE_MS = 6 * 60 * 60 * 1000;

export function isUpdateSnoozed(): boolean {
  try {
    return Date.now() < Number(localStorage.getItem(UPDATE_SNOOZE_KEY) ?? 0);
  } catch {
    return false;
  }
}

export function snoozeUpdate(): void {
  try {
    localStorage.setItem(UPDATE_SNOOZE_KEY, String(Date.now() + UPDATE_SNOOZE_MS));
  } catch {
    // localStorage unavailable (private mode / blocked); snooze just won't persist.
  }
}

/**
 * Poll version.json and call `onUpdateAvailable` when a newer build goes live.
 * Checks on an interval and whenever the tab regains focus/visibility — the
 * moment a long-lived stale tab matters most. Keeps polling while the update
 * prompt is snoozed so the toast can reappear after the snooze window. Returns
 * a cleanup function for unmount.
 */
export function startVersionWatcher(onUpdateAvailable: (info: VersionInfo) => void): () => void {
  let baseline = CURRENT_BUILD_ID || null;
  let stopped = false;
  let inFlight = false;
  let updateLogged = false;
  let intervalId: number | undefined;
  let probeTimeoutId: number | undefined;

  function stop(): void {
    if (stopped) return;
    stopped = true;
    if (intervalId !== undefined) window.clearInterval(intervalId);
    if (probeTimeoutId !== undefined) window.clearTimeout(probeTimeoutId);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
  }

  async function check(): Promise<void> {
    if (stopped || inFlight || document.hidden) return;
    inFlight = true;
    try {
      const info = await fetchLiveVersion();
      const live = info && fingerprint(info);
      if (!live) return;
      if (!baseline) {
        // No baked id (dev/preview): adopt the first reading as the baseline.
        baseline = live;
        return;
      }
      if (live !== baseline && info) {
        if (isUpdateSnoozed()) return;
        if (!updateLogged) {
          updateLogged = true;
          logger.info('New build detected; prompting reload', { from: baseline, to: live });
        }
        onUpdateAvailable(info);
      }
    } finally {
      inFlight = false;
    }
  }

  function onVisible(): void {
    if (!document.hidden) void check();
  }

  intervalId = window.setInterval(() => void check(), POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  // Defer the first probe so it doesn't compete with first paint / boot fetches.
  probeTimeoutId = window.setTimeout(() => void check(), INITIAL_PROBE_DELAY_MS);

  return stop;
}
